import { useQuery } from "@apollo/client";
import { CRISP_IDENTITY } from "@hey-lady/gql/queries/crisp";
import { useEffect, useRef } from "react";
import { useAuth } from "./user";

interface CrispIdentityQueryData {
  crispIdentity: {
    email: string;
    signature: string;
  } | null;
}

const pushCrisp = (command: unknown[]): void => {
  if (typeof window === "undefined") return;
  // window.$crisp is initialized in index.html and stays pushable after the Crisp
  // client loads (it may be replaced with a non-Array pushable object, so do NOT
  // guard with Array.isArray — see existing $crisp usage in helpers/utils.ts).
  window.$crisp.push(command);
};

/**
 * Sync the authenticated user's identity to the Crisp chatbox:
 *  - After login: reset any pre-existing anonymous Crisp session, then push the
 *    signed (verified) email so Crisp verifies it immediately (no refresh needed).
 *  - On logout: reset the Crisp session so no member's identity persists on a
 *    shared/logged-out browser.
 *
 * If the API returns `null` (identity verification not configured), we
 * intentionally do NOT push an unverified email — Hugo then has no verified
 * member to look up, which is the safe default.
 *
 * Note: the operator widget (docs/crisp-widget-integration-plan.md) keys everything on
 * the verified email that this hook pushes — no other data needs to be pushed to Crisp.
 */
export const useCrispIdentitySync = (): void => {
  const { authenticated, loading } = useAuth();
  const { data } = useQuery<CrispIdentityQueryData>(CRISP_IDENTITY, {
    skip: !authenticated,
  });
  const identifiedRef = useRef(false);
  // True once we've observed the user in a *settled* logged-out state (not the
  // brief loading window on a fresh page load). Used to detect an in-page login
  // so we can discard the anonymous Crisp session before applying the verified
  // identity — without resetting on a normal refresh (which would drop session
  // continuity for returning members).
  const sawLoggedOutRef = useRef(false);

  useEffect(() => {
    const identity = data?.crispIdentity;

    // A settled logged-out state means the visitor is genuinely anonymous, so the
    // Crisp script (loaded unconditionally in index.html) has an anonymous session
    // running. On a full page refresh, auth resolves loading -> authenticated
    // without ever hitting this, so sawLoggedOutRef stays false there.
    if (!authenticated && !loading) {
      sawLoggedOutRef.current = true;
    }

    if (authenticated && identity?.email && identity.signature) {
      if (!identifiedRef.current) {
        // In-page login: setting a verified email into the anonymous session that
        // already exists does not reliably flip Crisp's verification, so reset to a
        // fresh session first — mirroring a full page refresh (which works).
        if (sawLoggedOutRef.current) {
          pushCrisp(["do", "session:reset"]);
        }
        identifiedRef.current = true;
      }
      pushCrisp(["set", "user:email", [identity.email, identity.signature]]);
      return;
    }

    // Only reset when transitioning from identified -> logged out, so we never
    // wipe the chatbox during initial load or for anonymous visitors.
    if (!authenticated && !loading && identifiedRef.current) {
      pushCrisp(["do", "session:reset"]);
      identifiedRef.current = false;
    }
  }, [authenticated, loading, data]);
};
